import { AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  concatMap,
  debounce,
  delay,
  from,
  interval,
  Observable,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import { GenericEngagementModel } from '../../../../../../core/models/generic-engagement.model';
import { GenericEngagementService } from '../../../../settings/generic-engagement/generic-engagement.service';
import { map, take } from 'rxjs/operators';
import { SupplierModel } from '../../../../../../core/models/supplier.model';
import { SupplierService } from '../../../supplier/supplier.service';
import { ContractModel } from '../../../../../../core/models/contract.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SupplierContactModel } from '../../../../../../core/models/supplier-contact.model';
import { BaseContactType, ContactTypeEnum } from '../../../../../../core/enums/contact-type.enum';
import { SupplierContactCreateUpdateDeleteComponent } from '../../../supplier/modals/supplier-contact-create-update-delete/supplier-contact-create-update-delete.component';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { GenericPaymentConditionService } from '../../../../settings/generic-payment-condition/generic-payment-condition.service';
import { GenericPaymentConditionModel } from '../../../../../../core/models/generic-payment-condition.model';
import { DocumentElement, GedService } from '../../../../admin/playground/docuware-playground/ged.service';
import { GED_CONFIG, GedTypeEnum } from '../../../../../../core/enums/ged-type.enum';
import { FileSaverService } from 'ngx-filesaver';
import { ContractService } from '../../contract.service';
import { IDocument, IDocumentsQueryResult } from '../../../../../../core/ui/ged-ui/interface/docuware.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { ContractValidityEnum } from '../../../../../../core/enums/contract.enum';
import { DialogConfirmComponent } from '../../../../../../../@vex/components/dialog-confirm/dialog-confirm.component';
import { ContractStatusEnum } from '../../../../../../core/enums/contract-status.enum';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { SupplierCreateUpdateDeleteComponent } from '../../../supplier/modals/supplier-create-update-delete/supplier-create-update-delete.component';
import { UserProfileModel } from '../../../../../../core/models/user-profile.model';
import { UserProfileService } from '../../../user-profile/user-profile.service';
import { GenericEngagementFormatEnum } from '../../../../../../core/enums/generic-engagement-format.enum';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { lockFormWhenReadOnly } from 'src/app/core/utils/form-permission.util';
import { AuthService } from '../../../../../../core/services/auth.service';
import { TimeUnit, translateTimeUnitObject } from '../../../../../../core/utils/time-utils';
import { ContractResumeCreateUpdateDeleteComponent } from '../../modals/contract-resume-create-update-delete/contract-resume-create-update-delete.component';

@Component({
  selector: 'vex-contract-details-resume',
  templateUrl: './contract-details-resume.component.html',
  styleUrls: ['./contract-details-resume.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
  encapsulation: ViewEncapsulation.None,
})
export class ContractDetailsResumeComponent implements OnInit, AfterViewInit, OnDestroy {
  private totalPercentage = 0;
  private gedType: GedTypeEnum;
  readonly ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  public form: UntypedFormGroup;
  public genericEngagement$: Observable<GenericEngagementModel[]>;
  public genericPaymentCondition$: Observable<GenericPaymentConditionModel[]>;
  public suppliers$: Observable<SupplierModel[]>;
  public supplierContacts$: Observable<SupplierContactModel[]>;
  public contractValidityEnum = Object.values(ContractValidityEnum);
  public defaultValidity = ContractValidityEnum.FIRM;
  public loading = true;
  public loadingSave = false;
  public loadingFile = false;
  public files: DocumentElement[] = [];
  documentTypes = ['Contrat', 'Avenant contrat'];
  private nbTotal$ = new BehaviorSubject<number>(0);
  private currentBar$ = new BehaviorSubject<number>(0);
  private nbErrors$ = new BehaviorSubject<number>(0);
  private subscription = new Subscription();
  public isQuote: boolean;
  public contract: ContractModel;
  public minNbDay = 0;
  public maxNbDay = 10;
  public isUpdateDisabledPermission = true;
  private id: string;
  private isCalculatingRenewal = false;
  ContractStatusEnum = ContractStatusEnum;
  ContractValidityEnum = ContractValidityEnum;
  public engagementContacts$: Observable<UserProfileModel[]>;
  hasGenericEngagement = false;
  readonly TimeUnit = TimeUnit;

  constructor(
    private genericEngagementService: GenericEngagementService,
    private supplierService: SupplierService,
    private genericPaymentConditionService: GenericPaymentConditionService,
    private dialog: MatDialog,
    private _gedService: GedService,
    public fileSaver: FileSaverService,
    public contractService: ContractService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private snackBarService: SnackbarService,
    private userProfileService: UserProfileService,
    public authService: AuthService,
  ) {
    const gedConfig = GED_CONFIG.contract;
    this.gedType = gedConfig.gedType;
    this._gedService.setUrl(gedConfig.apiUrl);
    this._gedService.setType(gedConfig.gedType);
  }

  ngOnInit(): void {
    this.checkPermission();
    this.initData();
    this.initForm();
  }

  ngAfterViewInit() {
    lockFormWhenReadOnly(this.form, this.isUpdateDisabledPermission);
  }

  async initData() {
    this.id = this.activatedRoute.snapshot.params?.id;

    this.genericEngagementAutoCompleteChange();
    this.supplierAutocompleteChange();
    //   this.genericPaymentConditionAutoCompleteChange();

    this.loadingFile = true;
    this.subscription.add(
      this._gedService.allDocumentsByPredikId(this.id).subscribe((doc: IDocumentsQueryResult) => {
        const filteredItems = doc.Items.filter(item =>
          item.Fields.some(field => field.FieldName === 'DOCUMENT_TYPE' && field.Item === 'Contrat'),
        );

        this.files = doc.Items.length > 0 ? [this.prepareDocument(filteredItems[0])] : [];

        this.loadingFile = false;
      }),
    );
  }

  public addSupplier() {
    const dialogRef = this.dialog.open(SupplierCreateUpdateDeleteComponent, {});

    dialogRef.afterClosed().subscribe((supplier: any) => {
      if (supplier) {
        this.form.get('supplier').setValue(supplier.data, { emitEvent: true });
        this.form.get('supplierContact').setValue(null);
      }
    });
  }

  checkPermission() {
    this.authService.getCurrentUserPermissions$().subscribe(permissions => {
      this.isUpdateDisabledPermission = !permissions.includes(ENUM_PERMISSIONS.UPDATE_CONTRACT);
    });
  }

  private prepareDocument(document: IDocument): DocumentElement {
    return {
      id: document.FileCabinetId,
      DCWDOCID: document.Id,
      dateUpload: document.LastModified,
      originalFileName: document.Title,
      size: document.FileSize,
      contentType: document.ContentType,
      status: 'IMPORTING',
      _file: null,
      progress: new BehaviorSubject<number>(100),
      uploadError: '',
      errorType: '',
      type: this.parseDocumentType(document.ContentType),
    };
  }

  parseDocumentType(contentType: string): string {
    switch (contentType) {
      case 'application/pdf':
        return 'PDF';
      case 'image/jpeg':
        return 'JPEG';
      case 'image/jpg':
        return 'JPG';
      case 'image/png':
        return 'PNG';
      default:
        return '';
    }
  }

  private initForm() {
    this.subscription.add(
      this.contractService.entity$.subscribe((contract: ContractModel) => {
        this.contract = contract;
        console.log(contract, 'voici le contract parent ');
        if (contract) {
          this.engagementContactsAutoCompleteChange();
          //        this.supplierContactsAutocompleteChange();

          if (contract.paymentCondition) {
            const paymentCondition = contract.paymentCondition;
            contract.paymentCondition.displayProperty = paymentCondition.nbDay
              ? `${paymentCondition.label} (Maximum ${paymentCondition.nbDay} jours)`
              : `${paymentCondition.label}`;
            this.maxNbDay = paymentCondition.nbDay;
            if (contract.creationStep !== 1) {
              if (paymentCondition.nbDay) {
                contract.paymentCondition.displayProperty = paymentCondition.label.replace(
                  'X',
                  contract.nbDay.toString(),
                );
              }
            }
          }

          console.log('contract', contract);
          this.hasGenericEngagement = contract.genericEngagement !== null && contract.genericEngagement !== undefined;
        }

        this.loading = false;
      }),
    );
  }

  public genericEngagementAutoCompleteChange(state: string = '', contractFormat?: any) {
    const prefixPattern = /^(C\s*\/\s*|D\s*\/\s*|I\s*-\s*)/i;
    const prefixedLabel = state.match(prefixPattern)?.[0] || null;
    this.genericEngagement$ = this.contractService.entity$.pipe(
      take(1),
      switchMap((contract: ContractModel) => {
        const isQuote = !!contract.quoteId;

        return this.genericEngagementService
          .findAll(null, null, 'label', 'asc', state, { inUserPerimeter: true, prefixedLabel })
          .pipe(
            map(genericEngagement => {
              return genericEngagement.data
                .filter(item => (isQuote ? item.format === GenericEngagementFormatEnum.QUOTE : true))
                .map(item => {
                  switch (item.format) {
                    case GenericEngagementFormatEnum.QUOTE:
                      item.label = `D / ${item.label}`;
                      break;
                    case GenericEngagementFormatEnum.CONTRACT:
                      item.label = `C / ${item.label}`;
                      break;
                    case GenericEngagementFormatEnum.INTERNE:
                      item.label = `I- ${item.label}`;
                      break;
                  }
                  return item;
                });
            }),
          );
      }),
    );
  }

  public engagementContactsAutoCompleteChange(state: any = '') {
    this.engagementContacts$ = this.userProfileService
      .findAll(null, null, null, null, state, {
        isEngagementContacts: this.contract?.genericEngagement?.engagementCategory?._id,
      })
      .pipe(
        map(userProfile => {
          return userProfile.data.map(x => {
            return x;
          });
        }),
      );
  }

  public addDenunciationDate(): void {
    const sub = this.dialog
      .open(ContractResumeCreateUpdateDeleteComponent, {
        data: { contract: this.contract },
      })
      .afterClosed()
      .subscribe(result => {});
    this.subscription.add(sub);
  }

  public genericPaymentConditionAutoCompleteChange(state: any = '') {
    this.genericPaymentCondition$ = this.genericPaymentConditionService.findAll(null, null, null, null, state).pipe(
      map(genericPaymentCondition => {
        return genericPaymentCondition.data.map(x => {
          if (x.nbDay) {
            x.displayProperty = `${x.label} (Maximum ${x.nbDay} jours)`;
          } else {
            x.displayProperty = `${x.label}`;
          }
          return x;
        });
      }),
    );
  }

  public supplierAutocompleteChange(state: any = '') {
    this.suppliers$ = this.supplierService.findAll(null, null, null, null, state).pipe(map(x => x.data));
  }

  updateStep(step: number) {
    this.contractService.updateStep(this.id, { creationStep: step }).subscribe(res => {
      this.router.navigate(['/management/engagement/', this.id, 'perimeter']).then();
    });
  }

  async switchToUpdateMode() {
    this.dialog.open(DialogConfirmComponent, {
      data: {
        description:
          'Êtes-vous sûr de vouloir modifier les informations ? Attention cela peut avoir des conséquences sur les autres étapes.',
        cancelText: 'ANNULER',
        title: 'Modifier les informations',
        validText: 'CONFIRMER',
        call$: this.contractService.updateStep(this.id, { creationStep: 1 }),
      },
    });
  }

  validForm() {
    this.nbTotal$.next(this.files.length);
    this.totalPercentage = 100 * this.files.length;
    this.currentBar$.next(0);
    this.nbErrors$.next(0);

    this.loadingSave = true;
    from(this.files)
      .pipe(concatMap(item => of(item).pipe(delay(500))))
      .subscribe(
        document => {
          document.status = 'ON_LOAD';
          const documentElement = this._gedService.uploadDocument(
            document,
            {
              documentType: 'Contrat',
              predikId: this.id,
            },
            this.loadingSave,
          );

          documentElement.progress.pipe(debounce(() => interval(100))).subscribe(value => {
            if (value === 500) {
              this.loadingSave = false;
            }
            if (this.files[this.files.length - 1].id === document.id && value === 101) {
              this.initData();
            }
          });
        },
        error => console.error('toto'),
      );
  }

  fileDropped($event: any) {
    if ($event.length > 1) {
      this.files = [this.files[this.files.length - 1]];
    }
    this.subscription.add(
      this._gedService
        .allDocumentsByPredikId(this.id)
        .pipe(
          switchMap((res: IDocumentsQueryResult) => {
            if (res.Items.length > 0) {
              const doc: any = this._gedService.mapToDataSource(res);
              return this._gedService.deleteDocument(doc[0].DWDOCID);
            }
            return of('No document');
          }),
        )
        .subscribe((res: IDocumentsQueryResult) => {
          console.log('RES', res);
        }),
    );
    this.validForm();
  }

  downloadDocument(id) {
    this._gedService.downloadDocument(id).subscribe((res: any) => {
      this.fileSaver.save(res.body, res.headers.get('filename'));
    });
  }

  get isInternalContract(): boolean {
    return !!this.contract?.supportEstablishment;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
